/**
 * Hook: Modal state for Add/Edit, Duplicate, and Delete facility.
 * Single Responsibility: open/close and selected entity per modal type.
 * Interface Segregation: each modal exposes only what the UI needs (isOpen, open, close, entity).
 */

import { useState, useCallback } from 'react';
import { getLocationsByFacility } from '../../../services/locationsService';

/**
 * Fetch locations count for a facility (for duplicate modal copy).
 * @param {string} facilityId
 * @returns {Promise<number>}
 */
async function fetchLocationsCountForFacility(facilityId) {
  try {
    const response = await getLocationsByFacility(facilityId);
    return (response.data || []).length;
  } catch {
    return 0;
  }
}

export function useFacilityModals() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [facilityToEdit, setFacilityToEdit] = useState(null);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [facilityToDuplicate, setFacilityToDuplicate] = useState(null);
  const [facilityLocationsCount, setFacilityLocationsCount] = useState(0);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState(null);

  const openAddModal = useCallback(() => {
    setFacilityToEdit(null);
    setIsAddModalOpen(true);
  }, []);

  const closeAddModal = useCallback(() => {
    setIsAddModalOpen(false);
    setFacilityToEdit(null);
  }, []);

  const openEditModal = useCallback((facility) => {
    setFacilityToEdit(facility);
    setIsAddModalOpen(true);
  }, []);

  const openDuplicateModal = useCallback(async (facility) => {
    setFacilityToDuplicate(facility);
    setIsDuplicateModalOpen(true);
    const count = await fetchLocationsCountForFacility(facility.id);
    setFacilityLocationsCount(count);
  }, []);

  const closeDuplicateModal = useCallback(() => {
    setIsDuplicateModalOpen(false);
    setFacilityToDuplicate(null);
    setFacilityLocationsCount(0);
  }, []);

  const openDeleteModal = useCallback((facility) => {
    setFacilityToDelete(facility);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setFacilityToDelete(null);
  }, []);

  return {
    isAddModalOpen,
    facilityToEdit,
    openAddModal,
    closeAddModal,
    openEditModal,
    isDuplicateModalOpen,
    facilityToDuplicate,
    facilityLocationsCount,
    openDuplicateModal,
    closeDuplicateModal,
    isDeleteModalOpen,
    facilityToDelete,
    openDeleteModal,
    closeDeleteModal,
  };
}
